import { type ContextType, useCallback, useContext } from 'react';
import { useRouter } from 'expo-router';

import { LinkingContext } from 'expo-router/react-navigation';

import { useIsFirstAuthAttempt } from '../../../context/AuthMachine';
import { useNavigationReset } from '../../../routing/hooks/useNavigationReset';

// ─────────────────────────────────────────────────────────────────────────────

export const useRedirectAfterAuthStateChange = (params: Params = {}) => {
  const { shouldResetNavigation } = params;

  const router = useRouter();
  const resetNavigation = useNavigationReset();
  const { options: linkingOptions } = useContext(LinkingContext);
  const isFirstAuthAttempt = useIsFirstAuthAttempt();

  return useCallback(
    (href: string | undefined) => {
      const target = checkIfSafeInternalHref(href) ? href : '/';
      const navigationOptions = { withAnchor: target.startsWith(ACCOUNT_PATH_PREFIX) };

      // NOTE: Cold start has no stale stack to clear, so we navigate directly.
      if (isFirstAuthAttempt) {
        router.navigate(target as never, navigationOptions);

        return;
      }

      // NOTE: Customers who were already signed in have no pre-login screens to clear.
      if (!shouldResetNavigation) {
        router.dismissTo(target as never, navigationOptions);

        return;
      }

      /**
       * Mid-session login (signed-out → signed-in): replace the whole
       * navigation state with fresh stacks that end on the target, in a single
       * action, so neither the auth screens nor pre-login screens are left
       * behind.
       *
       * NOTE: A reset followed by a separate navigate can race, and `replace` or
       *       `navigate` from inside the auth modal mount a second tab tree.
       *       Both crash react-native-screens on iOS.
       */
      const linkedState = linkingOptions?.getStateFromPath?.(target, linkingOptions.config);
      const resetState = getResetState({ linkedState });

      if (!resetState) {
        router.dismissTo(target as never, navigationOptions);

        return;
      }

      resetNavigation(resetState as never);
    },
    [router, resetNavigation, linkingOptions, isFirstAuthAttempt, shouldResetNavigation],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfSafeInternalHref(href: string | undefined): href is string {
  if (typeof href !== 'string' || href.length === 0) return false;

  // Must start with a single `/` — `//` is a protocol-relative URL.
  return href.startsWith('/') && !href.startsWith('//');
}

/**
 * Linked states start at expo-router's root slot, which renders the root
 * layout. The reset replaces the root layout's routes, so they get fresh keys
 * and the pre-login screens unmount while the slot itself stays mounted.
 *
 * Expo Router 58 no longer adds the layouts' `unstable_settings` anchors when it
 * parses a path, so `(tabs)` under a modal and the Account screen under an
 * account route are added back here.
 */
function getResetState(params: GetResetStateParams) {
  const { linkedState } = params;

  const linkedRootRoute = linkedState?.routes[0];
  const linkedRoutes = linkedRootRoute?.state?.routes ?? [];

  if (linkedRootRoute?.name !== ROOT_SLOT_ROUTE_NAME) {
    return;
  }

  if (linkedRoutes.length === 0) {
    return;
  }

  const routes = withTabsAnchor(linkedRoutes.map(withAccountAnchor));

  return { index: routes.length - 1, routes };
}

function withTabsAnchor(routes: readonly LinkedRoute[]) {
  if (routes[0]?.name === TABS_ROUTE_NAME) {
    return routes;
  }

  return [{ name: TABS_ROUTE_NAME }, ...routes];
}

function withAccountAnchor(route: LinkedRoute): LinkedRoute {
  if (route.name !== TABS_ROUTE_NAME || !route.state) {
    return route;
  }

  return {
    ...route,
    state: { ...route.state, routes: route.state.routes.map(withAccountScreenAnchor) },
  };
}

function withAccountScreenAnchor(route: LinkedRoute): LinkedRoute {
  const accountRoutes = route.state?.routes ?? [];

  if (route.name !== ACCOUNT_ROUTE_NAME || accountRoutes[0]?.name === ACCOUNT_ANCHOR_ROUTE_NAME) {
    return route;
  }

  const routes = [{ name: ACCOUNT_ANCHOR_ROUTE_NAME }, ...accountRoutes];

  return { ...route, state: { ...route.state, index: routes.length - 1, routes } };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ACCOUNT_PATH_PREFIX = '/account/';
const ROOT_SLOT_ROUTE_NAME = '__root';
const TABS_ROUTE_NAME = '(tabs)';
const ACCOUNT_ROUTE_NAME = 'account';
const ACCOUNT_ANCHOR_ROUTE_NAME = 'index';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  /**
   * Replaces the whole navigation with fresh stacks ending on the target.
   * Only for customers who just signed in.
   */
  shouldResetNavigation?: boolean;
};

type GetResetStateParams = {
  linkedState: ReturnType<NonNullable<LinkingOptions['getStateFromPath']>>;
};

type LinkedRoute = {
  name: string;
  params?: object;
  state?: { index?: number; routes: readonly LinkedRoute[] };
};

type LinkingOptions = NonNullable<ContextType<typeof LinkingContext>['options']>;
